import { t } from 'i18next';
import { Points } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../../coordinate-manager';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import {
  findCrossedLineSegmentIdFromIntersection,
  findLineSegmentIdFromIntersection,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import {
  findPointIdFromIntersection,
  PointId,
} from '../../../../../../elements/design/design-node/utils/point-node';
import { AttributeData, AttributeId } from '../../../../../../elements/design/skymap-extras';
import { ActionName, DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';
import {
  createNewPoint,
  setToolEditIntersectionToPoint as setToolIntersection,
} from '../../utils/point';

/**
 * Distance treshold, if the point is closer than this distance
 * to the start of the line being created it is not placed.
 */
const distanceTreshold = 0.001;

export const createPoint = (
  tool: DesignTool,
  options?: {
    attributes?: Map<AttributeId, AttributeData>;
    pointId?: PointId;
    allowPointIntersection?: boolean;
  },
): ContextMenuItem => {
  const startCoordinate = isDefined(tool.currentPoint)
    ? CoordinateManager.getInstance().localToProjectCoords(tool.currentPoint.clone())
    : undefined;

  // A point where a line of the design ends on another one (a T) is used, to break the other line.
  const intersectedPointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
  const breaksCrossedLine =
    isDefined(intersectedPointId) &&
    isDefined(findCrossedLineSegmentIdFromIntersection(tool.asset.designNode, tool.intersection));

  const blockedByIntersectedPoint = options?.allowPointIntersection
    ? false
    : tool.intersection?.object instanceof Points && !breaksCrossedLine;

  // The start of a line being created becomes a point, so it is treated like an existing point.
  const lineOrigin =
    tool.actionInProgress?.actionName === ActionName.CreateLine
      ? tool.actionInProgress.origin
      : undefined;
  const blockedByLineOrigin =
    isDefined(lineOrigin) &&
    isDefined(tool.currentPoint) &&
    tool.currentPoint.distanceTo(lineOrigin) < distanceTreshold;

  const disabled = !isDefined(startCoordinate) || blockedByIntersectedPoint || blockedByLineOrigin;

  const intersectedLineSegmentId = findLineSegmentIdFromIntersection(
    tool.asset.designNode,
    tool.intersection,
  );
  const crossedLineSegmentId = findCrossedLineSegmentIdFromIntersection(
    tool.asset.designNode,
    tool.intersection,
  );

  return {
    title: t('contextMenu.create.items.point', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      const activeAttributes = tool.getActiveAttributes();
      for (const attribute of (
        options?.attributes ?? new Map<AttributeId, AttributeData>()
      ).entries()) {
        activeAttributes.set(attribute[0], attribute[1]);
      }

      let changes: RevertChange[] = [];

      if (!blockedByIntersectedPoint) {
        addAndCommitAction(tool, {
          commit: () => {
            const result = createNewPoint(
              tool,
              startCoordinate,
              intersectedLineSegmentId,
              options?.pointId ?? (breaksCrossedLine ? intersectedPointId : undefined),
              activeAttributes,
              crossedLineSegmentId,
            );
            changes = result.revertChanges;
            tool.asset.designNode.refresh();
            tool.asset.refreshMetadata();
            setToolIntersection(tool, result.id);
          },
          rollback: () => {
            tool.asset.designNode.revert(changes);
            tool.asset.designNode.refresh();
            tool.asset.refreshMetadata();
          },
        });
      }

      tool.contextMenuCallback?.();
    },
    disabled,
    hotkey: 'P',
  };
};
